// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="../../../../node_modules/mux-embed/dist/types/mux-embed.d.ts"/>
import { useCallback, useEffect, useRef } from "react";
import Hls from "hls.js";
import mux from "mux-embed";

import { useEmbedParametersContext } from "../../contexts/EmbedParametersContext";
import { usePrevious } from "../../helpers/commonUtils";
import { usePlayerStore } from "../../store/usePlayerStore";

import {
  AudioTrackChange,
  getAudioTrackData,
  getPlayerVersion,
  getSelectedAudioTrack,
  getSubtitlesData,
  isMuxEnabled,
  resolveExperimentName,
  resolveMediaType,
  resolvePlayername,
  SubtitlesChange
} from "./mux-helpers";

export const useMux = ({
  hls,
  mediaElement,
  type
}: {
  hls?: Hls;
  mediaElement?: HTMLMediaElement;
  type: "audio" | "hlsVideo" | "nativeVideo";
}): void => {
  const { getState, useStoreState } = usePlayerStore();
  const audioTracks = useStoreState.audioTracks();
  const isDestroyed = useStoreState.isDestroyed();
  const programId = useStoreState.programId();
  const selectedAudioTrackId = useStoreState.selectedAudioTrackId();
  const selectedSubtitles = useStoreState.selectedSubtitles();
  const subtitles = useStoreState.subtitles();

  const previousProgramId = usePrevious(programId);

  const { analytics, language, preload } = useEmbedParametersContext();

  const audioTrackChanges = useRef<AudioTrackChange[]>([]);
  const subtitlesChanges = useRef<SubtitlesChange[]>([]);

  const muxSessionVideo = useRef<HTMLMediaElement>(undefined);

  const getMediaMetadata = useCallback(() => {
    const state = getState();
    const media = state.media;
    return {
      custom_9: resolveMediaType(media?.contentType),
      experiment_name: resolveExperimentName(),
      player_autoplay_on: state.isAutoplay,
      player_init_time: state.mediaLoadingStartTimestamp,
      sub_property_id: state.isSimulcast ? state.serviceId : undefined,
      video_cdn: "akamai",
      video_duration: state.durationInSeconds
        ? state.durationInSeconds * 1000 // in milliseconds
        : undefined,
      video_encoding_variant: media?.mediaId?.split("-")[0],
      video_id: media?.programId,
      video_series: media?.series?.id,
      video_stream_type: state.isSimulcast
        ? "broadcast"
        : state.isLive
          ? "webcast"
          : "on-demand",
      video_title: media?.title?.fin || media?.title?.swe,
      ...getAudioTrackData(
        [],
        getSelectedAudioTrack(state.audioTracks, state.selectedAudioTrackId)
      ),
      ...getSubtitlesData([], state.selectedSubtitles)
    };
  }, [getState]);

  const startSession = useCallback(
    (element: HTMLMediaElement) => {
      muxSessionVideo.current = element;

      mux.monitor(element, {
        data: {
          env_key: __MUX_ENV_KEY,
          player_language_code: language,
          player_name: resolvePlayername(type),
          player_preload_on: preload === "auto",
          player_version: getPlayerVersion(),
          ...getMediaMetadata()
        },
        disableCookies: true,
        Hls: type === "hlsVideo" ? Hls : undefined,
        hlsjs: type === "hlsVideo" ? hls : undefined,
        respectDoNotTrack: true
      });
    },
    [getMediaMetadata, hls, language, preload, type]
  );

  const endSession = useCallback(() => {
    muxSessionVideo.current?.mux?.destroy();
    muxSessionVideo.current = undefined;
  }, []);

  useEffect(() => {
    // Video element will rerender when media is changed
    // This hook run on first render and always when mediaElement has been changed (= video has been changed)
    if (
      isMuxEnabled(analytics) &&
      mediaElement &&
      mediaElement !== muxSessionVideo.current &&
      (type !== "hlsVideo" || hls)
    ) {
      endSession();
      startSession(mediaElement);
    }
  }, [hls, startSession, type, mediaElement, endSession, analytics]);

  useEffect(() => {
    if (isDestroyed && muxSessionVideo.current) {
      endSession();
    }
  }, [endSession, isDestroyed]);

  useEffect(() => {
    // Audio element does not rerender on media change
    // Use mux videochange event to change session on mux
    if (
      type === "audio" &&
      programId !== previousProgramId &&
      muxSessionVideo.current
    ) {
      mux.emit(muxSessionVideo.current, "videochange", getMediaMetadata());
    }
  }, [getMediaMetadata, previousProgramId, programId, type]);

  useEffect(() => {
    if (muxSessionVideo.current) {
      const selectedAudioTrack = getSelectedAudioTrack(
        audioTracks,
        selectedAudioTrackId
      );

      if (selectedAudioTrack !== undefined) {
        audioTrackChanges.current = [
          { ...selectedAudioTrack, time: Date.now() },
          ...audioTrackChanges.current
        ];

        mux.emit(
          muxSessionVideo.current,
          "hb",
          getAudioTrackData(audioTrackChanges.current, selectedAudioTrack)
        );
      }
    }
  }, [audioTracks, selectedAudioTrackId]);

  useEffect(() => {
    if (muxSessionVideo.current && selectedSubtitles !== undefined) {
      subtitlesChanges.current = [
        { ...selectedSubtitles, time: Date.now() },
        ...subtitlesChanges.current
      ];

      mux.emit(
        muxSessionVideo.current,
        "hb",
        getSubtitlesData(subtitlesChanges.current, selectedSubtitles)
      );
    }
  }, [selectedSubtitles, subtitles]);
};
